// Recessed brass-fitted segmented control (the ArenaStore currency-tab
// recipe). The trough reads as a groove cut into the panel; the active
// pill sits in it as a lit brass plate that travels between segments.
// Shared by the hub Hall header and the looking-for-a-match panel.
import { TabBar } from './TabBar';
import { GOLD, innerRadius } from '../../lib/medievalTheme';
import { playUI, UI } from '../../utils/arena/uiSounds';

// Concentric radius step-down inside the 0.5cqw glass panels: controls
// round at 0.375cqw, and pills inside a 0.125cqw-padded trough at
// 0.25cqw (0.375 − 0.125), so every nesting level shares one centre.
export const PANEL_CONTROL_RADIUS = 'var(--radius-md)';
export const PANEL_CONTROL_PILL_RADIUS = 'var(--radius-sm)';
export const ROW_HEIGHT_CQW = '1.75cqw';
export const NATIVE_FONT_PX = 10;

export const TAB_TROUGH_STYLE = {
  background: 'url("/tex-noise.webp"), linear-gradient(180deg, rgba(6,5,3,0.5) 0%, rgba(12,9,5,0.4) 100%)',
  border: `1px solid ${GOLD} 0.16)`,
  boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(0,0,0,0.25), 0 1px 0 rgba(255,220,140,0.05)',
  padding: '0.125cqw',
};

export const TAB_PILL_ACTIVE = {
  color: '#f0d990',
  background: 'url("/tex-noise.webp"), linear-gradient(180deg, rgba(212,168,67,0.15) 0%, rgba(180,140,55,0.08) 50%, rgba(120,85,30,0.11) 100%)',
  border: `1px solid ${GOLD} 0.35)`,
  textShadow: '0 0 10px rgba(232,213,160,0.3), 0 1px 2px rgba(0,0,0,0.6)',
  boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.14), inset 0 -1px 0 rgba(0,0,0,0.28), 0 0 12px rgba(212,168,67,0.13)',
};

// The transparent border keeps active/inactive geometry identical so
// pills don't shift by 1px when the selection moves.
export const TAB_PILL_INACTIVE = {
  color: 'rgba(166,160,155,0.55)',
  background: 'transparent',
  border: '1px solid transparent',
  textShadow: '0 1px 2px rgba(0,0,0,0.7)',
  boxShadow: 'none',
};

// Active pill inside a TabBar: the plate travels as its own element, so
// the button keeps only the lit-label treatment.
export const TAB_PILL_ACTIVE_TEXT = {
  color: TAB_PILL_ACTIVE.color,
  background: 'transparent',
  border: '1px solid transparent',
  textShadow: TAB_PILL_ACTIVE.textShadow,
};

// The travelling plate itself: TAB_PILL_ACTIVE minus what the label owns.
export const TAB_PILL_INDICATOR = {
  background: TAB_PILL_ACTIVE.background,
  border: TAB_PILL_ACTIVE.border,
  boxShadow: TAB_PILL_ACTIVE.boxShadow,
};

// Fixed-native-size label scaled with the canvas, so pill text width is
// exactly proportional to the control at every canvas size.
export function ScaledLabel({ children, weight = 600 }) {
  return (
    <span
      style={{
        fontSize: `${NATIVE_FONT_PX}px`,
        transform: 'scale(var(--canvas-scale, 1))',
        transformOrigin: 'center',
        whiteSpace: 'nowrap',
        lineHeight: 1,
        fontWeight: weight,
        letterSpacing: '0.04em',
      }}
    >
      {children}
    </span>
  );
}

/**
 * Segmented pills on the recessed trough. `fill` stretches the control to
 * its container with equal segments; otherwise each segment is at least
 * `minCol` wide and the control hugs its content.
 */
export function PillTabs({ tabs, value, onChange, minCol = '5.5cqw', renderLabel, fill = false, disabled = false, height = ROW_HEIGHT_CQW, radius = PANEL_CONTROL_RADIUS }) {
  const pillRadius = innerRadius(radius, TAB_TROUGH_STYLE.padding);
  return (
    <TabBar
      className={`${fill ? 'grid w-full' : 'inline-grid'} items-center overflow-hidden shrink-0`}
      variant="plate"
      activeIndex={tabs.findIndex((t) => t.value === value)}
      indicatorStyle={{ ...TAB_PILL_INDICATOR, borderRadius: pillRadius }}
      style={{
        ...TAB_TROUGH_STYLE,
        borderRadius: radius,
        height,
        gridTemplateColumns: fill ? `repeat(${tabs.length}, 1fr)` : `repeat(${tabs.length}, minmax(${minCol}, auto))`,
        opacity: disabled ? 0.4 : 1,
        pointerEvents: disabled ? 'none' : undefined,
        transition: 'opacity 200ms ease',
      }}
    >
      {tabs.map((t) => (
        <button
          key={t.value}
          type="button"
          data-tab-item
          aria-pressed={value === t.value}
          className="grid place-items-center px-2 overflow-hidden h-full"
          style={{ ...(value === t.value ? TAB_PILL_ACTIVE_TEXT : TAB_PILL_INACTIVE), borderRadius: pillRadius }}
          onClick={() => { playUI(UI.CLICK); onChange(t.value); }}
        >
          <ScaledLabel>{renderLabel ? renderLabel(t) : t.label}</ScaledLabel>
        </button>
      ))}
    </TabBar>
  );
}

/**
 * Multi-select sibling of PillTabs: every pressed segment is lit, no
 * travelling plate. `value` is an array; `onChange` gets the next array.
 */
export function PillToggles({ tabs, value = [], onChange, height = ROW_HEIGHT_CQW, minOne = true, radius = PANEL_CONTROL_RADIUS }) {
  const on = new Set(value);
  const pillRadius = innerRadius(radius, TAB_TROUGH_STYLE.padding);
  return (
    <div
      className="grid w-full items-center overflow-hidden"
      style={{ ...TAB_TROUGH_STYLE, borderRadius: radius, height, gridTemplateColumns: `repeat(${tabs.length}, 1fr)`, gap: '0.125cqw' }}
    >
      {tabs.map((t) => {
        const active = on.has(t.value);
        return (
          <button
            key={t.value}
            type="button"
            aria-pressed={active}
            className="grid place-items-center px-2 overflow-hidden h-full active:scale-[0.97]"
            style={{
              ...(active ? TAB_PILL_ACTIVE : TAB_PILL_INACTIVE),
              borderRadius: pillRadius,
              transition: 'background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
            }}
            onClick={() => {
              if (active && minOne && on.size === 1) return;
              playUI(UI.CLICK);
              const next = new Set(on);
              if (active) next.delete(t.value); else next.add(t.value);
              onChange(tabs.map((x) => x.value).filter((v) => next.has(v)));
            }}
          >
            <ScaledLabel>{t.label}</ScaledLabel>
          </button>
        );
      })}
    </div>
  );
}
